import { logger } from "@/utils/logger";
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { cn } from '@/lib/utils';
import { useFormAutoCollapseControl } from '@/contexts/FormAutoCollapseContext';

interface IsolatedDatePickerProps {
  value?: string;
  onChange?: (date: string) => void;
  disabled?: (date: Date) => boolean;
  placeholder?: string;
  className?: string;
  error?: boolean;
}

export const IsolatedDatePicker: React.FC<IsolatedDatePickerProps> = ({
  value,
  onChange,
  disabled,
  placeholder = "Choose a date",
  className,
  error = false,
}) => {
  logger.log('🔴 DatePicker render - error:', error, 'value:', value);
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value ? new Date(value) : null
  );
  const { setTemporaryDisabled } = useFormAutoCollapseControl();

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.d2cbot-isolated-datepicker')) {
        setIsOpen(false);
        // Re-enable auto-collapse when closing via outside click
        setTimeout(() => setTemporaryDisabled(false), 100);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    onChange?.(dateString);
    setIsOpen(false);
    // Re-enable auto-collapse after selection
    setTimeout(() => setTemporaryDisabled(false), 100);
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDisplayDate = (date: Date) => {
    return date.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).split('-').reverse().join('/');
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const isDateDisabled = (date: Date) => {
    if (disabled) {
      return disabled(date);
    }
    return false;
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} />);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isDisabled = isDateDisabled(date);
      const isSelected = selectedDate && isSameDay(date, selectedDate);

      days.push(
        <button
          key={day}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isDisabled) {
              handleDateSelect(date);
            }
          }}
          disabled={isDisabled}
          className={cn(
            "d2cbot-calendar-day",
            "w-8 h-8 text-sm rounded-sm flex items-center justify-center",
            "transition-colors duration-200",
            "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
            isSelected
              ? "bg-black text-white"
              : isDisabled
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-900 hover:bg-gray-100 cursor-pointer"
          )}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <div className={cn("d2cbot-isolated-datepicker relative", className)}>
      <Button
        type="button"
        variant="outline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isOpen) {
                // Disable auto-collapse when opening picker
                setTemporaryDisabled(true);
              }
              setIsOpen(!isOpen);
            }}
        className={cn(
          "d2cbot-date-trigger w-full justify-start text-left font-normal h-7 text-sm",
          !selectedDate && "text-gray-500"
        )}
      >
        <FontAwesomeIcon icon={faCalendar} className="mr-2 h-3 w-3" />
        {selectedDate ? formatDisplayDate(selectedDate) : placeholder}
      </Button>

      {isOpen && (
        <div
          className={cn(
            "d2cbot-calendar-popup absolute bottom-full left-0 mb-1 z-[99999]",
            "bg-white border border-gray-200 rounded-md shadow-lg p-3",
            "min-w-[280px]"
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '0',
            marginBottom: '4px',
            zIndex: 99999,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            padding: '12px'
          }}
        >
          {/* Calendar Header */}
          <div className="d2cbot-calendar-header flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                previousMonth();
              }}
              className="d2cbot-nav-button p-1 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="d2cbot-month-year font-medium text-sm">
              {currentMonth.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
              })}
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                nextMonth();
              }}
              className="d2cbot-nav-button p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Day Labels */}
          <div className="d2cbot-day-labels grid grid-cols-7 gap-1 mb-2">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
              <div
                key={day}
                className="d2cbot-day-label text-xs font-medium text-gray-500 text-center p-1"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="d2cbot-calendar-grid grid grid-cols-7 gap-1">
            {renderCalendar()}
          </div>
        </div>
      )}
    </div>
  );
};